import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography, Paper, IconButton, Checkbox } from '@mui/material';
import RGL, { WidthProvider, Layout } from 'react-grid-layout';
import { FieldWithId } from './MetadataInterfaces';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveIcon from '@mui/icons-material/OpenWith';
import AddIcon from '@mui/icons-material/Add';

const ReactGridLayout = WidthProvider(RGL);

interface FieldListProps {
  fields: FieldWithId[];
  onUpdateFields: (updatedFields: FieldWithId[]) => void;
  onEditField: (field: FieldWithId) => void;
  onDeleteField: (fieldId: string) => void;
  onMoveField: (field: FieldWithId) => void;
  isSelectionMode: boolean;
  selectedItems: string[];
  onSelectionChange: (id: string) => void;
  onAddField: (afterFieldId: string) => void;
}

const FieldList: React.FC<FieldListProps> = ({
  fields,
  onUpdateFields,
  onEditField,
  onDeleteField,
  onMoveField,
  isSelectionMode,
  selectedItems,
  onSelectionChange,
  onAddField,
}) => {
  const isSingleLayout = useCallback((field: FieldWithId) => {
    return (
      field.Layout.toLowerCase() === 'single' ||
      field.Layout.toLowerCase() === 'fluid-single' ||
      field.Control === 'MultiSelectField'
    );
  }, []);

  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [isHoveringEdge, setIsHoveringEdge] = useState(false);

  const backgroundColor = (field: FieldWithId) => {
    if (field.GroupingKey) return 'rgba(0, 0, 0, 0.04)';
    if (field.HideFromMainForm) return 'warning.light';
    return 'white';
  };

  const handleMouseMove = (
    event: React.MouseEvent<HTMLDivElement>,
    fieldId: string,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isNearRightEdge = rect.right - event.clientX <= 50;
    const isInVerticalRange =
      event.clientY >= rect.top + (rect.height - 30) / 2 &&
      event.clientY <= rect.top + (rect.height + 30) / 2;

    setHoveredField(fieldId);
    setIsHoveringEdge(isNearRightEdge && isInVerticalRange);
  };

  const handleMouseLeave = () => {
    setHoveredField(null);
    setIsHoveringEdge(false);
  };

  const generateLayout = useCallback(() => {
    let y = 0;
    let x = 0;
    const layout: Layout[] = [];

    const wouldCauseNewLine = (field: FieldWithId) => {
      return isSingleLayout(field) || field.Layout === 'paired';
    };

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const isSingle = isSingleLayout(field);

      if (field.Layout === 'paired') {
        layout.push({ i: field.id, x: 0, y, w: 1, h: 1 });
        if (i + 1 < fields.length) {
          layout.push({ i: fields[i + 1].id, x: 1, y, w: 1, h: 1 });
          i++; // Skip the next field as we've already added it
        }
        y++;
        x = 0;
      } else if (isSingle) {
        layout.push({ i: field.id, x: 0, y, w: 2, h: 1 });
        y++;
        x = 0;
      } else {
        // fluid
        const nextField = i + 1 < fields.length ? fields[i + 1] : null;
        const isLastField = i === fields.length - 1;
        const groupingKey =
          typeof field.GroupingKey === 'undefined' || field.GroupingKey === ''
            ? undefined
            : field.GroupingKey;

        const nextGroupingKey =
          !nextField ||
          typeof nextField.GroupingKey === 'undefined' ||
          nextField.GroupingKey === ''
            ? undefined
            : nextField.GroupingKey;
        const nextWouldCauseNewLine =
          (nextField && wouldCauseNewLine(nextField)) ||
          groupingKey !== nextGroupingKey;

        if (x === 0 && (isLastField || nextWouldCauseNewLine)) {
          // Make this fluid field take up full width
          layout.push({ i: field.id, x: 0, y, w: 2, h: 1 });
          y++;
          x = 0;
        } else {
          layout.push({ i: field.id, x, y, w: 1, h: 1 });
          if (x === 1) {
            y++;
            x = 0;
          } else {
            x = 1;
          }
        }
      }
    }

    return layout;
  }, [fields, isSingleLayout]);

  const layout = useMemo(() => generateLayout(), [generateLayout]);

  const onLayoutChange = (newLayout: Layout[]) => {
    const sortedFields = newLayout
      .sort((a, b) => {
        if (a.y === b.y) {
          return a.x - b.x;
        }
        return a.y - b.y;
      })
      .map((item) => fields.find((f) => f.id === item.i))
      .filter((field): field is FieldWithId => field !== undefined);

    onUpdateFields(sortedFields);
  };

  return (
    <ReactGridLayout
      className="layout"
      layout={layout}
      cols={2}
      rowHeight={100}
      width={500}
      onLayoutChange={onLayoutChange}
      isResizable={false}
      draggableHandle=".drag-handle"
    >
      {fields.map((field) => (
        <div
          key={field.id}
          onMouseMove={(e) => handleMouseMove(e, field.id)}
          onMouseLeave={handleMouseLeave}
          style={{ position: 'relative' }}
        >
          <Paper
            elevation={1}
            sx={{
              p: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: backgroundColor(field),
              transition: 'all 0.3s ease',
              width:
                hoveredField === field.id && isHoveringEdge
                  ? 'calc(100% - 50px)'
                  : '100%',
              border: field.HideFromMainForm ? '1px dashed' : 'none',
              borderColor: field.HideFromMainForm ? 'warning.main' : 'unset',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {isSelectionMode && (
                  <Checkbox
                    checked={selectedItems.includes(field.id)}
                    onChange={() => onSelectionChange(field.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <Typography
                  variant="subtitle2"
                  sx={{
                    flexGrow: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    userSelect: 'none',
                    mr: 1,
                  }}
                >
                  {field.Label || field.Key}{' '}
                  {field.HideFromMainForm && (
                    <span style={{ color: 'rgba(0,0,0,0.5)' }}>(Hidden)</span>
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexShrink: 0 }}>
                <IconButton onClick={() => onMoveField(field)} size="small">
                  <MoveIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => onEditField(field)} size="small">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => onDeleteField(field.id)}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 'auto' }}
            >
              Layout: {field.Layout}, Control: {field.Control || 'Default'}
            </Typography>
            {field.GroupingKey && (
              <Typography variant="caption" color="primary">
                Grouped: {field.GroupingKey}
              </Typography>
            )}
            <div
              className="drag-handle"
              style={{
                marginTop: '4px',
                cursor: 'move',
                padding: '4px',
                alignSelf: 'flex-end',
                userSelect: 'none',
              }}
            >
              ⋮
            </div>
          </Paper>
          {hoveredField === field.id && isHoveringEdge && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '50px',
                backgroundColor: 'green',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                opacity: 1,
                cursor: 'pointer',
              }}
              onClick={() => onAddField(field.id)}
            >
              <AddIcon sx={{ color: 'white' }} />
            </Box>
          )}
        </div>
      ))}
    </ReactGridLayout>
  );
};

export default FieldList;
